import _, { isEmpty } from 'lodash';
import React from 'react';

import {
  queryGetSubscription,
  queryMyProfile,
  queryUpdateSubscription,
  queryGetFacilitySettings,
} from '../../../Queries';

import { getFacility } from '../../../Utils/facility';

import ProcessingIndicator from '../../Common/ProcessingIndicator';
import ActionButton from '../../Common/ActionButton';
import NoticeBlock from '../../Common/NoticeBlock';

import Field from '../../Fields/Field';

import '../Page.scss';
import './SubscriptionRecurringPayments.scss';

const profileQueryFields = ['gpi_cards', 'gpi_bank_accounts'];

const subscriptionQueryFields = [
  { payor: ['cognito_sub'] },
  'auto_pay',
  'payment_method_id',
];

const updateSubscriptionFields = ['auto_pay', 'id'];

class SubscriptionRecurringPayments extends React.Component {
  state = {
    pageInitializationComplete: false,
    pageInitializationErrors: [],
    cognitoSub: null,
    facilityId: null,
    facility: null,
    subscriptionRecurringPaymentsInputs: {
      isRecurringPayment: false,
      paymentType: null,
      paymentMethodId: null,
    },
    bankDetails: [],
    creditCardDetails: [],
    saveInProgress: false,
    queryInProgress: false,
    success: false,
    showBankDetails: true,
  };

  constructor(props) {
    super(props);

    const urlSearchParams = new URLSearchParams(window.location.search);

    const facilityId = urlSearchParams.get('facility');
    this.state.facilityId = facilityId;
  }

  async componentDidMount() {
    try {
      let facility = null;
      let settingsResponse = null;
      const facilitySettingsFields = ['use_ach'];
      if (this.state.facilityId) {
        facility = await getFacility(this.state.facilityId);
        settingsResponse = await queryGetFacilitySettings(
          {
            facility_id: this.state.facilityId,
          },
          facilitySettingsFields
        );
      }
      await this.fetchPayor();
      this.setState({
        pageInitializationComplete: true,
        facility,
        showBankDetails: settingsResponse.data.getFacilitySettings.use_ach,
      });
    } catch (errors) {
      this.setState({
        pageInitializationComplete: true,
        pageInitializationErrors: errors.map(({ message }) => message),
      });
    }
  }

  async fetchPayor() {
    const response = await queryGetSubscription(
      {
        subscription_id: this.props.subscriptionId,
      },
      subscriptionQueryFields
    );
    if (response.errors) {
      throw response.errors.map((error) => error.message);
    }
    const cognitoSub =
      response.data.getSubscription.payor.cognito_sub;
    this.setState({ cognitoSub }, async () => {
      await this.getMyProfile();
      this.setRecurringPaymentInputs(response.data.getSubscription);
    });
  }

  setRecurringPaymentInputs(subscription) {
    const { creditCardDetails, subscriptionRecurringPaymentsInputs } =
      this.state;

    const isCreditCard = creditCardDetails.find(
      (card) => card.token === subscription.payment_method_id
    );
    const paymentType = !isEmpty(isCreditCard) ? 'creditCard' : 'bankAccount';
    this.onPaymentMethodSelect(subscription.payment_method_id, 'creditCard');
    this.onPaymentMethodSelect(subscription.payment_method_id, 'bankAccount');

    const inputs = _.cloneDeep(subscriptionRecurringPaymentsInputs);
    inputs['isRecurringPayment'] = subscription.auto_pay;

    if (subscription.payment_method_id) {
      inputs['paymentMethodId'] = subscription.payment_method_id;
      inputs['paymentType'] = paymentType;
    }

    this.setState({ subscriptionRecurringPaymentsInputs: inputs });
  }

  async getMyProfile() {
    this.setState({
      queryInProgress: true,
    });
    if (this.state.cognitoSub) {
      const variables = {
        cognitoSub: this.state.cognitoSub,
      };
      const response = await queryMyProfile(variables, profileQueryFields);
      const bankDetails = response.data.myProfile.gpi_bank_accounts;
      const creditCardDetails = response.data.myProfile.gpi_cards;
      this.setState({ bankDetails, creditCardDetails });
    }
    this.setState({
      queryInProgress: false,
    });
  }

  handleRecurringPaymentInputs = (value, inputId) => {
    const inputs = _.cloneDeep(this.state.subscriptionRecurringPaymentsInputs);
    if (inputId === 'paymentMethodId') {
      this.onPaymentMethodSelect(value, inputs['paymentType']);
    }
    inputs[inputId] = value;

    this.setState({ subscriptionRecurringPaymentsInputs: inputs });
  };

  onPaymentMethodSelect = (value, type) => {
    const { bankDetails, creditCardDetails } = this.state;
    let cards = creditCardDetails;
    let accounts = bankDetails;
    if (type === 'creditCard') {
      cards = creditCardDetails.map((card) => {
        return {
          ...card,
          selected: card.token === value,
        };
      });
    } else {
      accounts = bankDetails.map((account) => {
        return {
          ...account,
          selected: account.token === value,
        };
      });
    }
    this.setState({ bankDetails: accounts, creditCardDetails: cards });
  };

  handleSaveRecurringPayments = async () => {
    this.setState({
      saveInProgress: true,
    });
    try {
      const {
        subscriptionRecurringPaymentsInputs: {
          isRecurringPayment,
          paymentType,
        },
        creditCardDetails,
        bankDetails,
      } = this.state;

      const variables = {
        subscription_id: this.props.subscriptionId,
        subscription: {
          auto_pay: isRecurringPayment,
        },
      };
      if (isRecurringPayment) {
        const paymentMethodId = paymentType === 'creditCard' ?
          creditCardDetails.find((card) => card.selected).token :
          bankDetails.find((account) => account.selected).token;

        variables.subscription['payment_method_id'] = paymentMethodId;
      }
      const response = await queryUpdateSubscription(
        variables,
        updateSubscriptionFields
      );
      if (response.errors) {
        throw response.errors[0];
      }
      this.setState({
        saveInProgress: false,
        success: true,
      });
    } catch (error) {
      alert(error.message);
      this.setState({
        saveInProgress: false,
        success: false,
      });
    }
  };

  render() {
    const {
      subscriptionRecurringPaymentsInputs,
      bankDetails,
      creditCardDetails,
      pageInitializationComplete,
      queryInProgress,
      saveInProgress,
      success,
      showBankDetails,
      facilityId,
    } = this.state;

    const isViewOnly = this.props.recurringPayment === 'DISPLAY_AND_VIEW_ONLY';
    const viewOnlyPaymentOption =
      this.props.paymentOptions === 'DO_NOT_DISPLAY';

    const { isRecurringPayment, paymentType } =
      subscriptionRecurringPaymentsInputs;

    const selectedCreditCard = creditCardDetails.find((card) => card.selected);
    const selectedBankAccount = bankDetails.find((account) => account.selected);

    const isDisabled = !isRecurringPayment ? false :
      paymentType === 'creditCard'
        ? isEmpty(creditCardDetails) || isEmpty(selectedCreditCard)
        : isEmpty(bankDetails) || isEmpty(selectedBankAccount);
    if (!pageInitializationComplete || queryInProgress) {
      return (
        <div className='parkchirp--page--user-message-container'>
          <ProcessingIndicator userMessage='Please Wait...' />
        </div>
      );
    }

    const path = `admin.php?page=parkchirp_subscription&subscription=${this.props.subscriptionId}&facility=${facilityId}#/payment-options`;

    return (
      <div className='parkchirp--page parkchirp--page--subscription-recurring-payments-panel'>
        <div>
          {!isRecurringPayment && <h2>Recurring payments is not enabled</h2>}
          <Field
            label='Recurring Payments'
            id='parkchirp--subscription-list-filter--account-id'
            layout='stacked'
          >
            <span className='parkchirp--input-checkbox-option'>
              <input
                type='checkbox'
                value={!isRecurringPayment}
                checked={isRecurringPayment}
                onChange={(e) => {
                  this.handleRecurringPaymentInputs(
                    e.target.value === 'false' ? false : true,
                    'isRecurringPayment'
                  );
                }}
                disabled={isViewOnly}
              />
              <label>Enable Recurring Payments?</label>
            </span>
          </Field>
        </div>
        {isRecurringPayment && (
          <div>
            <Field
              label='Choose a Payment Type'
              id='parkchirp--subscription-list-filter--account-id'
              containerClasses={[
                'parkchirp--subscription-recurring-payments-panel-fields',
              ]}
              layout='stacked'
            >
              <span className='parkchirp--input-checkbox-option'>
                <input
                  type='checkbox'
                  value={'creditCard'}
                  checked={paymentType === 'creditCard'}
                  onChange={(e) =>
                    this.handleRecurringPaymentInputs(
                      e.target.value,
                      'paymentType'
                    )
                  }
                  disabled={isViewOnly}
                />
                <label>Credit Card</label>
              </span>
              {showBankDetails && (
                <span className='parkchirp--input-checkbox-option'>
                  <input
                    type='checkbox'
                    value={'bankAccount'}
                    checked={paymentType === 'bankAccount'}
                    onChange={(e) =>
                      this.handleRecurringPaymentInputs(
                        e.target.value,
                        'paymentType'
                      )
                    }
                    disabled={isViewOnly}
                  />
                  <label>Bank Account</label>
                </span>
              )}
            </Field>
          </div>
        )}
        {paymentType !== null && isRecurringPayment && (
          <div>
            <Field
              label={
                paymentType === 'creditCard'
                  ? 'Choose a Credit Card'
                  : showBankDetails
                  ? 'Choose a Bank Account'
                  : ''
              }
              id='parkchirp--subscription-list-filter--account-id'
              containerClasses={[
                'parkchirp--subscription-recurring-payments-panel-fields',
              ]}
              layout='stacked'
            >
              {paymentType === 'creditCard'
                ? creditCardDetails.map((card) => (
                    <span className='parkchirp--input-checkbox-option'>
                      <input
                        type='checkbox'
                        value={card.token}
                        checked={card.selected}
                        onChange={(e) =>
                          this.handleRecurringPaymentInputs(
                            e.target.value,
                            'paymentMethodId'
                          )
                        }
                        disabled={isViewOnly}
                      />
                      <label>
                        {card.type} ending in {card.last4}, {card.exp_month}/
                        {card.exp_year}
                      </label>
                    </span>
                  ))
                : showBankDetails
                ? bankDetails.map((account) => (
                    <span className='parkchirp--input-checkbox-option'>
                      <input
                        type='checkbox'
                        value={account.token}
                        checked={account.selected}
                        onChange={(e) =>
                          this.handleRecurringPaymentInputs(
                            e.target.value,
                            'paymentMethodId'
                          )
                        }
                        disabled={isViewOnly}
                      />
                      <label>
                        {account.accountType} account ending in {account.last4}
                      </label>
                    </span>
                  ))
                : ''}
              <div
                className={`parkchirp--recurring-payment-action-button ${
                  !showBankDetails && paymentType !== 'creditCard'
                    ? 'hide-add-bank-account-button'
                    : ''
                }`}
              >
                <a
                  href={path}
                  disabled={isViewOnly || viewOnlyPaymentOption}
                  className={`button button-primary add-new-payment ${
                    isViewOnly || viewOnlyPaymentOption ? 'disable-button' : ''
                  }`}
                >
                  {paymentType === 'creditCard'
                    ? 'Add New Card'
                    : 'Add New Bank Account'}
                </a>

                {viewOnlyPaymentOption && (
                  <div className='parkchirp--payment-option-info'>
                    <NoticeBlock
                      type='message'
                      containerClasses={['parkchirp--sidebar-block']}
                    >
                      <p>
                        Check General Settings {'>'} Payment Application "Allow
                        Admin to Edit Payment Options" setting to "Display and
                        Edit" if you need to add a credit card or bank account
                      </p>
                    </NoticeBlock>
                  </div>
                )}
              </div>
            </Field>
          </div>
        )}
        <div className='parkchirp--recurring-payment-action-button'>
          <ActionButton
            id='parkchirp--invoice-refund-action-button'
            label='Save'
            onClick={this.handleSaveRecurringPayments}
            inProgressState={saveInProgress}
            disabled={isDisabled || isViewOnly}
          />
          {success && (
            <div className='parkchirp--recurring-payment-success-text'>
              <NoticeBlock
                type='message'
                containerClasses={['parkchirp--sidebar-block']}
              >
                <p>This subscription has been updated!</p>
              </NoticeBlock>
            </div>
          )}
        </div>
      </div>
    );
  }
}
export default SubscriptionRecurringPayments;
